import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    TextField,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    Switch,
    FormControlLabel,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Avatar,
    LinearProgress,
    Divider,
    Tab,
    Tabs,
    Badge
} from '@mui/material';

// icons
import {
    IconDatabase,
    IconPlus,
    IconEdit,
    IconTrash,
    IconCopy,
    IconCheck,
    IconX,
    IconRefresh,
    IconEye,
    IconEyeOff,
    IconSettings,
    IconKey,
    IconLock,
    IconServer,
    IconPlugConnected,
    IconAlertCircle,
    IconCode,
    IconTestPipe
} from '@tabler/icons-react';

const ConnectionsManager = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [createDialog, setCreateDialog] = useState(false);
    const [testDialog, setTestDialog] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [selectedConnection, setSelectedConnection] = useState(null);

    // Connection form state
    const [connectionForm, setConnectionForm] = useState({
        name: '',
        type: 'mongodb',
        host: 'localhost',
        port: '27017',
        database: '',
        username: '',
        password: '',
        ssl: false,
        authSource: 'admin',
        connectionString: ''
    });

    // Mock connections data
    const connections = [
        {
            id: 1,
            name: 'Production MongoDB',
            type: 'mongodb',
            host: 'mongodb.production.com',
            port: 27017,
            database: 'production_db',
            status: 'connected',
            lastConnected: '2 mins ago',
            ssl: true,
            poolSize: 100,
            activeConnections: 45
        },
        {
            id: 2,
            name: 'Analytics PostgreSQL',
            type: 'postgresql',
            host: 'postgres.analytics.com',
            port: 5432,
            database: 'analytics',
            status: 'connected',
            lastConnected: '5 mins ago',
            ssl: true,
            poolSize: 50,
            activeConnections: 23
        },
        {
            id: 3,
            name: 'Development MongoDB',
            type: 'mongodb',
            host: 'localhost',
            port: 27017,
            database: 'dev_db',
            status: 'disconnected',
            lastConnected: '2 hours ago',
            ssl: false,
            poolSize: 10,
            activeConnections: 0
        }
    ];

    // Connection pools
    const connectionPools = [
        { app: 'API Server', connections: 25, idle: 5, waiting: 0, status: 'healthy' },
        { app: 'Analytics Service', connections: 15, idle: 3, waiting: 2, status: 'healthy' },
        { app: 'Background Jobs', connections: 8, idle: 2, waiting: 0, status: 'healthy' },
        { app: 'Report Generator', connections: 5, idle: 1, waiting: 1, status: 'warning' }
    ];

    // API keys
    const apiKeys = [
        { id: 1, name: 'Production API', key: 'pk_live_••••••••••••', created: '2024-01-01', lastUsed: '2 hours ago', status: 'active' },
        { id: 2, name: 'Development API', key: 'pk_test_••••••••••••', created: '2024-01-10', lastUsed: 'Yesterday', status: 'active' },
        { id: 3, name: 'Mobile App', key: 'pk_mobile_••••••••••', created: '2024-01-15', lastUsed: 'Never', status: 'inactive' }
    ];

    const connectionSteps = ['Connection Type', 'Connection Details', 'Authentication', 'Test Connection'];

    const getDatabaseIcon = (type) => {
        switch (type) {
            case 'mongodb':
                return <IconDatabase />;
            case 'postgresql':
                return <IconServer />;
            case 'mysql':
                return <IconDatabase />;
            default:
                return <IconDatabase />;
        }
    };

    const handleCreateConnection = () => {
        setCreateDialog(true);
        setActiveStep(0);
    };

    const handleTestConnection = (connection) => {
        setSelectedConnection(connection);
        setTestDialog(true);
    };

    const handleFormChange = (field, value) => {
        setConnectionForm((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <Box>
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab label="Connections" />
                        <Tab label="Connection Pools" />
                        <Tab label="API Keys" />
                        <Tab label="Settings" />
                    </Tabs>
                </Box>

                <CardContent>
                    {/* Connections Tab */}
                    {tabValue === 0 && (
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography variant="h5">Database Connections</Typography>
                                <Button variant="contained" startIcon={<IconPlus />} onClick={handleCreateConnection}>
                                    New Connection
                                </Button>
                            </Box>

                            <Grid container spacing={3}>
                                {connections.map((connection) => (
                                    <Grid item xs={12} md={6} key={connection.id}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                border:
                                                    connection.status === 'connected'
                                                        ? `2px solid ${theme.palette.success.main}`
                                                        : '1px solid',
                                                borderColor:
                                                    connection.status === 'connected' ? theme.palette.success.main : theme.palette.divider
                                            }}
                                        >
                                            <CardContent>
                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                    <Box display="flex" gap={2}>
                                                        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                                                            {getDatabaseIcon(connection.type)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="h6">{connection.name}</Typography>
                                                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                                                <Chip
                                                                    label={connection.status}
                                                                    size="small"
                                                                    color={connection.status === 'connected' ? 'success' : 'default'}
                                                                    icon={
                                                                        connection.status === 'connected' ? (
                                                                            <IconPlugConnected size={14} />
                                                                        ) : (
                                                                            <IconX size={14} />
                                                                        )
                                                                    }
                                                                />
                                                                {connection.ssl && (
                                                                    <Chip label="SSL" size="small" icon={<IconLock size={14} />} />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                    <IconButton size="small">
                                                        <IconSettings />
                                                    </IconButton>
                                                </Box>

                                                <Divider sx={{ my: 2 }} />

                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Connection String
                                                        </Typography>
                                                        <Box display="flex" alignItems="center">
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                {connection.host}:{connection.port}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    navigator.clipboard.writeText(`${connection.host}:${connection.port}`)
                                                                }
                                                            >
                                                                <IconCopy size={14} />
                                                            </IconButton>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Database
                                                        </Typography>
                                                        <Typography variant="body2">{connection.database}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Active Connections
                                                        </Typography>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Typography variant="body2">
                                                                {connection.activeConnections} / {connection.poolSize}
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={(connection.activeConnections / connection.poolSize) * 100}
                                                                sx={{ width: 50, height: 4 }}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Last Connected
                                                        </Typography>
                                                        <Typography variant="body2">{connection.lastConnected}</Typography>
                                                    </Grid>
                                                </Grid>

                                                <Box display="flex" gap={1} mt={2}>
                                                    <Button
                                                        size="small"
                                                        startIcon={<IconTestPipe />}
                                                        onClick={() => handleTestConnection(connection)}
                                                    >
                                                        Test
                                                    </Button>
                                                    <Button size="small" startIcon={<IconCode />}>
                                                        Code
                                                    </Button>
                                                    <Button size="small" startIcon={<IconEdit />}>
                                                        Edit
                                                    </Button>
                                                    <Button size="small" color="error" startIcon={<IconTrash />}>
                                                        Delete
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {/* Connection Pools Tab */}
                    {tabValue === 1 && (
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Connection Pool Management
                            </Typography>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Monitor and manage database connection pools across your applications
                            </Alert>

                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Application</TableCell>
                                            <TableCell align="center">Active</TableCell>
                                            <TableCell align="center">Idle</TableCell>
                                            <TableCell align="center">Waiting</TableCell>
                                            <TableCell align="center">Pool Usage</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {connectionPools.map((pool) => (
                                            <TableRow key={pool.app}>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <IconServer size={18} />
                                                        {pool.app}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">{pool.connections}</TableCell>
                                                <TableCell align="center">{pool.idle}</TableCell>
                                                <TableCell align="center">
                                                    <Badge badgeContent={pool.waiting} color={pool.waiting > 0 ? 'warning' : 'default'}>
                                                        <span>{pool.waiting}</span>
                                                    </Badge>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={((pool.connections - pool.idle) / pool.connections) * 100}
                                                            sx={{ width: 100 }}
                                                            color={pool.status === 'warning' ? 'warning' : 'primary'}
                                                        />
                                                        <Typography variant="caption">
                                                            {Math.round(((pool.connections - pool.idle) / pool.connections) * 100)}%
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={pool.status}
                                                        size="small"
                                                        color={pool.status === 'healthy' ? 'success' : 'warning'}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small">
                                                        <IconRefresh />
                                                    </IconButton>
                                                    <IconButton size="small">
                                                        <IconSettings />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Grid container spacing={3} sx={{ mt: 2 }}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Pool Configuration
                                            </Typography>
                                            <List>
                                                <ListItem>
                                                    <ListItemText primary="Min Pool Size" secondary="10 connections" />
                                                    <ListItemSecondaryAction>
                                                        <IconButton size="small">
                                                            <IconEdit size={16} />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Max Pool Size" secondary="100 connections" />
                                                    <ListItemSecondaryAction>
                                                        <IconButton size="small">
                                                            <IconEdit size={16} />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Connection Timeout" secondary="30 seconds" />
                                                    <ListItemSecondaryAction>
                                                        <IconButton size="small">
                                                            <IconEdit size={16} />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Idle Timeout" secondary="10 minutes" />
                                                    <ListItemSecondaryAction>
                                                        <IconButton size="small">
                                                            <IconEdit size={16} />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Connection Health
                                            </Typography>
                                            <List>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconCheck color={theme.palette.success.main} />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Connection Availability" secondary="All pools are healthy" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconCheck color={theme.palette.success.main} />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Response Time" secondary="Average: 45ms" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconAlertCircle color={theme.palette.warning.main} />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Queue Wait Time" secondary="2 requests waiting" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconCheck color={theme.palette.success.main} />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Failed Connections" secondary="0 in last hour" />
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* API Keys Tab */}
                    {tabValue === 2 && (
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography variant="h5">API Key Management</Typography>
                                <Button variant="contained" startIcon={<IconKey />}>
                                    Generate New Key
                                </Button>
                            </Box>

                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Key</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell>Last Used</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {apiKeys.map((key) => (
                                            <TableRow key={key.id}>
                                                <TableCell>{key.name}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                            {key.key}
                                                        </Typography>
                                                        <IconButton size="small" onClick={() => navigator.clipboard.writeText(key.key)}>
                                                            <IconCopy size={14} />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{key.created}</TableCell>
                                                <TableCell>{key.lastUsed}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={key.status}
                                                        size="small"
                                                        color={key.status === 'active' ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small">
                                                        <IconEdit size={16} />
                                                    </IconButton>
                                                    <IconButton size="small" color="error">
                                                        <IconTrash size={16} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Alert severity="info" sx={{ mt: 3 }}>
                                API keys provide secure access to your database through the DataHub API. Keep them secret and rotate
                                regularly.
                            </Alert>
                        </Box>
                    )}

                    {/* Settings Tab */}
                    {tabValue === 3 && (
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Connection Settings
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Security Settings
                                            </Typography>
                                            <List>
                                                <ListItem>
                                                    <ListItemText primary="Require SSL/TLS" secondary="Enforce encrypted connections" />
                                                    <ListItemSecondaryAction>
                                                        <Switch defaultChecked />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="IP Whitelisting" secondary="Restrict connections by IP" />
                                                    <ListItemSecondaryAction>
                                                        <Switch defaultChecked />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Connection Encryption"
                                                        secondary="Encrypt connection strings at rest"
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Switch defaultChecked />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Audit Logging" secondary="Log all connection attempts" />
                                                    <ListItemSecondaryAction>
                                                        <Switch defaultChecked />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Default Connection Settings
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                label="Connection Timeout"
                                                defaultValue="30"
                                                type="number"
                                                sx={{ mb: 2 }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">seconds</InputAdornment>
                                                }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Query Timeout"
                                                defaultValue="120"
                                                type="number"
                                                sx={{ mb: 2 }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">seconds</InputAdornment>
                                                }}
                                            />
                                            <TextField fullWidth label="Max Retries" defaultValue="3" type="number" sx={{ mb: 2 }} />
                                            <FormControl fullWidth>
                                                <InputLabel>Read Preference</InputLabel>
                                                <Select defaultValue="primary" label="Read Preference">
                                                    <MenuItem value="primary">Primary</MenuItem>
                                                    <MenuItem value="primaryPreferred">Primary Preferred</MenuItem>
                                                    <MenuItem value="secondary">Secondary</MenuItem>
                                                    <MenuItem value="secondaryPreferred">Secondary Preferred</MenuItem>
                                                    <MenuItem value="nearest">Nearest</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                            <Box display="flex" justifyContent="flex-end" mt={3}>
                                <Button variant="contained" startIcon={<IconCheck />}>
                                    Save Settings
                                </Button>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Create Connection Dialog */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Connection</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {connectionSteps.map((label, index) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                    <StepContent>
                                        {index === 0 && (
                                            <Box>
                                                <FormControl fullWidth sx={{ mb: 2 }}>
                                                    <InputLabel>Database Type</InputLabel>
                                                    <Select
                                                        value={connectionForm.type}
                                                        label="Database Type"
                                                        onChange={(e) => handleFormChange('type', e.target.value)}
                                                    >
                                                        <MenuItem value="mongodb">
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <IconDatabase />
                                                                MongoDB
                                                            </Box>
                                                        </MenuItem>
                                                        <MenuItem value="postgresql">
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <IconServer />
                                                                PostgreSQL
                                                            </Box>
                                                        </MenuItem>
                                                        <MenuItem value="mysql">
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <IconDatabase />
                                                                MySQL
                                                            </Box>
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        )}
                                        {index === 1 && (
                                            <Box>
                                                <TextField
                                                    fullWidth
                                                    label="Connection Name"
                                                    value={connectionForm.name}
                                                    onChange={(e) => handleFormChange('name', e.target.value)}
                                                    sx={{ mb: 2 }}
                                                />
                                                <Grid container spacing={2}>
                                                    <Grid item xs={8}>
                                                        <TextField
                                                            fullWidth
                                                            label="Host"
                                                            value={connectionForm.host}
                                                            onChange={(e) => handleFormChange('host', e.target.value)}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            fullWidth
                                                            label="Port"
                                                            value={connectionForm.port}
                                                            onChange={(e) => handleFormChange('port', e.target.value)}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <TextField
                                                    fullWidth
                                                    label="Database"
                                                    value={connectionForm.database}
                                                    onChange={(e) => handleFormChange('database', e.target.value)}
                                                    sx={{ mt: 2 }}
                                                />
                                            </Box>
                                        )}
                                        {index === 2 && (
                                            <Box>
                                                <TextField
                                                    fullWidth
                                                    label="Username"
                                                    value={connectionForm.username}
                                                    onChange={(e) => handleFormChange('username', e.target.value)}
                                                    sx={{ mb: 2 }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    label="Password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={connectionForm.password}
                                                    onChange={(e) => handleFormChange('password', e.target.value)}
                                                    sx={{ mb: 2 }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                                    {showPassword ? <IconEyeOff /> : <IconEye />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={connectionForm.ssl}
                                                            onChange={(e) => handleFormChange('ssl', e.target.checked)}
                                                        />
                                                    }
                                                    label="Use SSL/TLS"
                                                />
                                            </Box>
                                        )}
                                        {index === 3 && (
                                            <Box textAlign="center" py={3}>
                                                <IconCheck size={48} color={theme.palette.success.main} />
                                                <Typography variant="h6" gutterBottom>
                                                    Connection Successful!
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Successfully connected to {connectionForm.type} database
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ mb: 2, mt: 2 }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => setActiveStep(activeStep + 1)}
                                                sx={{ mt: 1, mr: 1 }}
                                                disabled={index === connectionSteps.length - 1}
                                            >
                                                {index === connectionSteps.length - 1 ? 'Finish' : 'Continue'}
                                            </Button>
                                            <Button
                                                disabled={index === 0}
                                                onClick={() => setActiveStep(activeStep - 1)}
                                                sx={{ mt: 1, mr: 1 }}
                                            >
                                                Back
                                            </Button>
                                        </Box>
                                    </StepContent>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
                    {activeStep === connectionSteps.length - 1 && (
                        <Button variant="contained" onClick={() => setCreateDialog(false)}>
                            Save Connection
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Test Connection Dialog */}
            <Dialog open={testDialog} onClose={() => setTestDialog(false)}>
                <DialogTitle>Test Connection</DialogTitle>
                <DialogContent>
                    <Box py={3} textAlign="center">
                        <LinearProgress sx={{ mb: 2 }} />
                        <Typography>Testing connection to {selectedConnection?.name}...</Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ConnectionsManager;
